// AgentWalletChip — Phase 3 always-visible pool balance for the agent
// chrome. Mirrors the player's WalletChip footprint so the agent's
// shell shows the same continuous "wallet at a glance" affordance.
//
// Visual treatment matches the player's chip almost 1:1:
//   - 28-px pill with the 32-px coin protruding top, bottom, and ~4 px
//     to the left (`-ml-1`) — same "3-D coin sitting on the bar" trick
//     the in-game wallet pills use.
//   - NO left padding on the pill (`pl-0`) so the coin is the leftmost
//     thing the eye lands on.
//   - The coin asset is `/player/coin.png` — same file the player SPA
//     uses, shipped under agent's public/player/ for the agent SPA
//     too. Single source of truth for the platform coin.
//
// Differences from player WalletChip (intentional):
//   - Brand magenta pill gradient (player's is orange-brand). The
//     coin itself stays the same gold artwork — visual consistency
//     across the platform.
//   - Hidden when /admin/me-agent/wallet returns 403 (agent lacks
//     `earnings:view`) — graceful degradation, no broken pill.
//   - Negative balance: pill flips to a rose gradient + leading minus
//     so the agent immediately sees they're below zero (settlement
//     pushed them past their pool — paired with the margin-call gate).
//
// Tap: links to /profile/wallet (the agent's wallet panel — canonical
// path; /me/wallet is a legacy redirect we no longer point new links at).

import { Link } from 'react-router-dom';
import { useAgentWallet } from '../lib/agentWallet';

export default function AgentWalletChip() {
  const { balance, loading, available } = useAgentWallet();
  if (!available) return null;

  const isNegative = balance < 0;
  const display = loading
    ? '…'
    : Math.abs(balance).toLocaleString('en-IN', { maximumFractionDigits: 0 });

  return (
    <Link
      to="/profile/wallet"
      aria-label="Open my pool wallet"
      title={loading ? 'Loading wallet…' : `Pool balance: ${isNegative ? '−' : ''}₹${display}`}
      className={`inline-flex items-center gap-1 h-7 pl-0 pr-3 rounded-full
                  text-white font-bold text-sm
                  border shadow-md
                  active:scale-[0.97] transition select-none
                  ${isNegative
                    ? 'bg-gradient-to-br from-rose-500 to-rose-700 border-rose-700/60'
                    : 'bg-gradient-to-br from-brand-400 to-brand-600 border-brand-600/60'}`}
    >
      {/* 32-px coin on a 28-px pill + -4 px left pull → coin protrudes
          ~2 px above/below and ~4 px to the left. Same trick the
          player chip uses. */}
      <img
        src="/player/coin.png"
        alt=""
        aria-hidden
        draggable={false}
        className="w-8 h-8 -ml-1 shrink-0 drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]"
      />
      <span className="tabular-nums leading-none">
        {isNegative && !loading ? '−' : ''}{display}
      </span>
    </Link>
  );
}
